import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Send } from "lucide-react";
import { toast } from "sonner";
import { FadeUp } from "@/components/Reveal";
import { postNewsletter } from "@/lib/api";
import { goTool } from "@/lib/ui";

export default function FinalCTA() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await postNewsletter(email.trim());
      toast.success("You're on the list", { description: "Launch updates, no spam. Ever." });
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "That email didn't work. Try another.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative py-28 sm:py-36 border-t border-white/5 overflow-hidden" data-testid="final-cta-section">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(50% 60% at 50% 100%, rgba(0,255,102,0.09), transparent 70%)" }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="max-w-4xl">
          <FadeUp>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-neon mb-6 flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-neon pulse-dot" />
              Ready when you are
            </p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h2 className="font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl text-white">
              NEVER GO
              <br />
              <span className="text-outline-neon">'AWAY'</span> AGAIN<span className="text-neon">.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.18}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => goTool(navigate)}
                data-testid="final-cta-start-button"
                className="inline-flex items-center gap-2.5 rounded-full bg-neon text-ink font-bold px-8 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:bg-neonhover hover:shadow-[0_0_40px_rgba(0,255,102,0.5)]"
              >
                <Zap size={17} strokeWidth={2.5} />
                Go green now
              </button>
              <form onSubmit={subscribe} className="flex items-center gap-2 w-full sm:w-auto max-w-full" data-testid="newsletter-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@work.com — get launch updates"
                  data-testid="newsletter-input"
                  className="min-w-0 w-full sm:w-80 bg-surface border border-white/10 rounded-full px-5 py-4 text-sm text-white placeholder:text-dim focus:outline-none focus:border-neon/60 transition-colors"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="newsletter-submit-button"
                  className="w-12 h-12 shrink-0 rounded-full border border-neon/40 text-neon flex items-center justify-center transition-colors hover:bg-neon hover:text-ink disabled:opacity-50"
                  aria-label="Subscribe"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
